import { useCallback, useMemo, useState } from 'react';
import type { UseRichTextControllerProps, UseRichTextControllerReturn } from './types';

export function useRichTextController({
  value: controlledValue,
  defaultValue = '',
  editing: controlledEditing,
  defaultEditing = false,
  onSave,
  onCancel,
  onChange,
}: UseRichTextControllerProps = {}): UseRichTextControllerReturn {
  // Determine if components are controlled
  const isValueControlled = controlledValue !== undefined;
  const isEditingControlled = controlledEditing !== undefined;

  // Internal state for uncontrolled mode
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [internalEditing, setInternalEditing] = useState(defaultEditing);
  const [editingValue, setEditingValue] = useState('');

  // Compute effective values
  const value = isValueControlled ? controlledValue : internalValue;
  const isEditing = isEditingControlled ? controlledEditing : internalEditing;

  const startEditing = useCallback(() => {
    if (!isEditingControlled) {
      setInternalEditing(true);
    }
    // Initialize editing value with current content
    setEditingValue(value);
  }, [isEditingControlled, value]);

  const handleChange = useCallback(
    (newValue: string) => {
      setEditingValue(newValue);
      onChange?.(newValue);
    },
    [onChange],
  );

  const handleSave = useCallback(
    (finalValue?: string) => {
      const valueToSave = finalValue ?? editingValue;

      // Update internal state if uncontrolled
      if (!isValueControlled) {
        setInternalValue(valueToSave);
      }

      if (!isEditingControlled) {
        setInternalEditing(false);
      }

      // Clear editing value
      setEditingValue('');

      // Call save callback
      onSave?.(valueToSave);
    },
    [editingValue, isValueControlled, isEditingControlled, onSave],
  );

  const handleCancel = useCallback(() => {
    // Reset editing value to original
    setEditingValue(value);

    if (!isEditingControlled) {
      setInternalEditing(false);
    }

    onCancel?.();
  }, [value, isEditingControlled, onCancel]);

  return useMemo(
    () => ({
      value: isEditing ? editingValue : value,
      isEditing,
      startEditing,
      handleChange,
      handleSave,
      handleCancel,
    }),
    [value, editingValue, isEditing, startEditing, handleChange, handleSave, handleCancel],
  );
}
